import { createLazyFileRoute, Link } from "@tanstack/react-router";
import type { Decimal } from "decimal.js";
import { BarChartIcon, TableIcon } from "lucide-react";
import React from "react";
import { TableLayout, Virtualizer } from "react-aria-components";
import {
  Line,
  LineChart,
  Tooltip as RechartsTooltip,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";

import { trpc } from "@blis/trpc/react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Heading } from "@/components/ui/heading";
import { TableRAC } from "@/components/ui/table-rac";
import { Tab, Tabs, TabsList } from "@/components/ui/tabs";
import { Text } from "@/components/ui/text";
import {
  NonFocusableTooltipTarget,
  Tooltip,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/components/ui/utils";
import { format } from "@/lib/utils";
import {
  AvailableChartColors,
  type AvailableChartColorsKeys,
  constructCategoryColors,
  getColorClassName,
} from "@/utils/chart-utils";

export const Route = createLazyFileRoute("/_auth/_app/subjects/$id/results")({
  component: RouteComponent,
});

// Define the types for the data structure
type ResultEntry = {
  testSpecId: number;
  testSpecName: string;
  testSpecType: string;
  testResultSpecId: number;
  testResultSpecName: string;
  testResultSpecType: string;
  testResultSpec: {
    rangeMin: Decimal | null;
    rangeMax: Decimal | null;
  };
  results: Array<{
    id: number;
    testOrderId: number;
    accessionId: number;
    accessionDate: Date;
    enteredAt: Date | null;
    resultText: string | null;
    resultNumeric: { toString(): string } | null;
    resultHla: string[] | null;
    finalResult: unknown | null;
    finalResultNumeric: { toString(): string } | null;
    finalResultHla: string[] | null;
    diff?: number;
    percentChange?: number | null;
    percentFromInitial?: number | null;
  }>;
};

// Custom tooltip component for the chart
interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    payload: {
      value: number | null;
      date: Date;
      enteredAt: Date | null;
      percentChange: number | null;
    };
  }>;
  label?: string;
}

const CustomTooltip = ({ active, payload, label }: TooltipProps) => {
  if (active && payload?.length) {
    const data = payload[0].payload;
    return (
      <div
        className="bg-background border rounded p-2 shadow-md text-sm"
        style={{
          zIndex: 10000,
          position: "relative",
          backgroundColor: "white",
          boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
        }}
      >
        <p className="font-medium">
          {data.value !== null ? data.value.toFixed(2) : "N/A"}
        </p>
        {data.percentChange !== undefined && data.percentChange !== null && (
          <p
            className={
              data.percentChange > 0
                ? "text-green-500"
                : data.percentChange < 0
                  ? "text-red-500"
                  : ""
            }
          >
            {data.percentChange > 0 ? "+" : ""}
            {data.percentChange.toFixed(2)}% from previous
          </p>
        )}
        <p className="text-muted-foreground">
          {new Date(data.date).toLocaleDateString()}
          {data.enteredAt &&
            ` (entered ${new Date(data.enteredAt).toLocaleString()})`}
        </p>
      </div>
    );
  }
  return null;
};

// Custom SparkLineChart with reference ranges
const RangeSparkLineChart = React.forwardRef<
  HTMLDivElement,
  {
    data: Record<string, unknown>[];
    categories: string[];
    index: string;
    rangeMin: number | null;
    rangeMax: number | null;
    className?: string;
    connectNulls?: boolean;
  }
>((props, ref) => {
  const {
    data = [],
    categories = [],
    index,
    rangeMin,
    rangeMax,
    className,
    connectNulls = false,
  } = props;

  const categoryColors = constructCategoryColors(
    categories,
    AvailableChartColors,
  );

  // Calculate min and max values for chart domain
  const values = data
    .map((d) => d.value as number | null)
    .filter((v): v is number => v !== null);
  const dataMin = Math.min(...values);
  const dataMax = Math.max(...values);

  // Add padding to the domain
  const padding = (dataMax - dataMin) * 0.1;
  const minValue = Math.min(
    dataMin - padding,
    rangeMin !== null ? rangeMin - padding : dataMin - padding,
  );
  const maxValue = Math.max(
    dataMax + padding,
    rangeMax !== null ? rangeMax + padding : dataMax + padding,
  );

  return (
    <div
      ref={ref}
      className={className}
      aria-label={`Chart showing values from ${dataMin} to ${dataMax}${
        rangeMin !== null ? `, with lower reference range of ${rangeMin}` : ""
      }${rangeMax !== null ? `, with upper reference range of ${rangeMax}` : ""}`}
    >
      <ResponsiveContainer>
        <LineChart
          data={data}
          margin={{
            bottom: 1,
            left: 1,
            right: 1,
            top: 1,
          }}
        >
          <XAxis hide dataKey={index} />
          <YAxis hide domain={[minValue, maxValue]} />
          <RechartsTooltip
            content={<CustomTooltip />}
            wrapperStyle={{
              zIndex: 99999,
              pointerEvents: "none",
              position: "absolute",
              filter: "drop-shadow(0 0 2px rgba(0,0,0,0.2))",
            }}
            cursor={{ stroke: "#666", strokeWidth: 1, strokeDasharray: "3 3" }}
            position={{ x: 0, y: -100 }}
            allowEscapeViewBox={{ x: true, y: true }}
            isAnimationActive={false}
            animationDuration={0}
          />

          {/* Reference lines for min and max range */}
          {rangeMin !== null && (
            <ReferenceLine
              y={rangeMin}
              stroke="rgba(239, 68, 68, 0.5)"
              strokeDasharray="3 3"
              ifOverflow="extendDomain"
            />
          )}
          {rangeMax !== null && (
            <ReferenceLine
              y={rangeMax}
              stroke="rgba(239, 68, 68, 0.5)"
              strokeDasharray="3 3"
              ifOverflow="extendDomain"
            />
          )}

          {/* Add shaded areas for out-of-range values */}
          {rangeMin !== null && (
            <ReferenceLine
              y={rangeMin}
              stroke="none"
              ifOverflow="extendDomain"
              segment={[
                { x: 0, y: minValue },
                { x: "100%", y: minValue },
              ]}
              fill="rgba(239, 68, 68, 0.1)"
              fillOpacity={1}
              shape={({ points }) => {
                // Check if points is an array and has at least 2 elements
                if (!Array.isArray(points) || points.length < 2) {
                  // biome-ignore lint/complexity/noUselessFragments: return type
                  return <></>; // Return null if points is not valid
                }

                const [p1, p2] = points;
                return (
                  <rect
                    x={p1.x}
                    y={p1.y}
                    width={p2.x - p1.x}
                    height={rangeMin - minValue}
                    fill="rgba(239, 68, 68, 0.1)"
                  />
                );
              }}
            />
          )}
          {rangeMax !== null && (
            <ReferenceLine
              y={rangeMax}
              stroke="none"
              ifOverflow="extendDomain"
              segment={[
                { x: 0, y: maxValue },
                { x: "100%", y: maxValue },
              ]}
              fill="rgba(239, 68, 68, 0.1)"
              fillOpacity={1}
              shape={({ points }) => {
                // Check if points is an array and has at least 2 elements
                if (!Array.isArray(points) || points.length < 2) {
                  // biome-ignore lint/complexity/noUselessFragments: return type
                  return <></>; // Return null if points is not valid
                }

                const [p1, p2] = points;
                return (
                  <rect
                    x={p1.x}
                    y={rangeMax}
                    width={p2.x - p1.x}
                    height={maxValue - rangeMax}
                    fill="rgba(239, 68, 68, 0.1)"
                  />
                );
              }}
            />
          )}

          {/* Data lines */}
          {categories.map((category) => (
            <Line
              className={cn(
                getColorClassName(
                  categoryColors.get(category) as AvailableChartColorsKeys,
                  "stroke",
                ),
              )}
              dot={false}
              strokeOpacity={1}
              key={category}
              name={category}
              type="linear"
              dataKey={category}
              stroke=""
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
              isAnimationActive={false}
              connectNulls={connectNulls}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
});

RangeSparkLineChart.displayName = "RangeSparkLineChart";

function RouteComponent() {
  const subjectId = Route.useParams().id;
  const [tab, setTab] = React.useState<
    "all" | "SEROLOGY" | "CLINICAL" | "MICROBIOLOGY"
  >("all");
  const [comparison, setComparison] = React.useState<"previous" | "initial">(
    "previous",
  );
  const [viewMode, setViewMode] = React.useState<"table" | "chart">("table");

  const { data } = trpc.subjects.testOrderResults.useQuery(subjectId);

  const max =
    data?.reduce((acc, curr) => {
      return Math.max(acc, curr.results.length);
    }, 0) ?? 0;

  const layout = React.useMemo(
    () =>
      new TableLayout({
        rowHeight: 44,
        headingHeight: 44,
      }),
    [],
  );

  const filteredData = React.useMemo(() => {
    if (tab === "all") return data ?? [];
    return (
      data?.filter((item) => {
        return item.testSpecType === tab;
      }) ?? []
    );
  }, [data, tab]);

  // Prepare chart data for each result entry
  const prepareChartData = (item: ResultEntry) => {
    if (item.testResultSpecType !== "NUMERIC" || item.results.length <= 1) {
      return null;
    }

    // Format data for SparkLineChart with additional data for tooltips
    return item.results.map((result, index, array) => {
      // Calculate percent change from previous
      let percentChange: number | null = null;
      if (index > 0 && result.resultNumeric && array[index - 1].resultNumeric) {
        const current = Number.parseFloat(result.resultNumeric.toString());
        // We've already checked array[index-1].resultNumeric is not null in the if condition
        const previousResult = array[index - 1].resultNumeric;
        if (previousResult) {
          const previous = Number.parseFloat(previousResult.toString());
          // Now percentChange is explicitly assigned a number
          percentChange = ((current - previous) / previous) * 100;
        }
      }

      return {
        date: result.accessionDate,
        enteredAt: result.enteredAt,
        value: result.resultNumeric
          ? Number.parseFloat(result.resultNumeric.toString())
          : null,
        percentChange,
        percentFromInitial: result.percentFromInitial,
      };
    });
  };

  const toggleViewMode = () => {
    setViewMode(viewMode === "table" ? "chart" : "table");
  };

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 border-b">
        <Heading level={1}>Donor results</Heading>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onPress={toggleViewMode}>
            {viewMode === "table" ? (
              <BarChartIcon size={16} />
            ) : (
              <TableIcon size={16} />
            )}
          </Button>
          <Tabs
            variant="segment"
            selectedKey={comparison}
            onSelectionChange={(val) => setComparison(val as typeof comparison)}
          >
            <TabsList>
              <Tab id="previous">vs previous</Tab>
              <Tab id="initial">vs initial</Tab>
            </TabsList>
          </Tabs>
          <Tabs
            variant="segment"
            selectedKey={tab}
            onSelectionChange={(val) => setTab(val as typeof tab)}
          >
            <TabsList>
              <Tab id="all">All</Tab>
              <Tab id="SEROLOGY">Serology</Tab>
              <Tab id="CLINICAL">Clinical</Tab>
              {/* <Tab id="HLA">HLA</Tab> */}
              <Tab id="MICROBIOLOGY">Micro</Tab>
            </TabsList>
          </Tabs>
        </div>
      </div>
      {viewMode === "table" ? (
        <Virtualizer layout={layout}>
          <TableRAC className="flex-1 overflow-auto scroll-pt-11">
            <TableRAC.Header className="h-11 bg-background">
              <TableRAC.Row>
                <TableRAC.Column isRowHeader width={200}>
                  Test
                </TableRAC.Column>
                <TableRAC.Column width={200}>Result</TableRAC.Column>
                <TableRAC.Column>Results</TableRAC.Column>
                {Array.from({ length: max - 1 }).map((_, index) => (
                  <TableRAC.Column key={index}>{""}</TableRAC.Column>
                ))}
              </TableRAC.Row>
            </TableRAC.Header>
            <TableRAC.Body
              items={filteredData}
              dependencies={[filteredData, comparison]}
            >
              {(item) => (
                <TableRAC.Row
                  id={item.testResultSpecId}
                  style={{ width: "inherit", height: "inherit" }}
                  className="*:not-last:border-r"
                >
                  <TableRAC.Cell className="truncate">
                    {item.testSpecName}
                  </TableRAC.Cell>
                  <TableRAC.Cell className="truncate">
                    {item.testResultSpecName}
                  </TableRAC.Cell>
                  {Array.from({ length: max }).map((_, i) => {
                    const result = item.results[i];
                    if (!result)
                      return (
                        <TableRAC.Cell
                          key={i}
                          className="whitespace-nowrap h-full bg-accent"
                        >
                          {""}
                        </TableRAC.Cell>
                      );

                    // Check if text result has changed from previous
                    const prevResult = i > 0 ? item.results[i - 1] : null;
                    const hasTextChanged =
                      result.resultText &&
                      prevResult?.resultText &&
                      result.resultText !== prevResult.resultText;

                    return (
                      <TableRAC.Cell
                        key={result.id}
                        className={cn(
                          "whitespace-nowrap p-0!",
                          hasTextChanged && "bg-amber-50 dark:bg-amber-950/30",
                        )}
                      >
                        <TooltipTrigger delay={300}>
                          <NonFocusableTooltipTarget>
                            <span className="cursor-default block w-full h-full px-3 py-3">
                              {hasTextChanged && (
                                <span
                                  className="inline-block w-2 h-2 rounded-full bg-amber-500 mr-2"
                                  aria-label="Result changed"
                                />
                              )}
                              {result.resultText ||
                                result.resultNumeric?.toString() || (
                                  <Text size={"1"} muted>
                                    pending
                                  </Text>
                                )}
                              {comparison === "previous" &&
                                result.percentChange !== null &&
                                result.percentChange !== undefined && (
                                  <Badge
                                    className="ml-2"
                                    color={
                                      Math.abs(result.percentChange) > 50
                                        ? "red"
                                        : "gray"
                                    }
                                  >
                                    {result.percentChange > 0 ? "+" : ""}
                                    {result.percentChange.toFixed(1)}%
                                  </Badge>
                                )}
                              {comparison === "initial" &&
                                result.percentFromInitial !== null &&
                                result.percentFromInitial !== undefined && (
                                  <Badge
                                    className="ml-2"
                                    color={
                                      Math.abs(result.percentFromInitial) > 50
                                        ? "red"
                                        : "gray"
                                    }
                                  >
                                    {result.percentFromInitial > 0 ? "+" : ""}
                                    {result.percentFromInitial.toFixed(1)}%
                                  </Badge>
                                )}
                            </span>
                          </NonFocusableTooltipTarget>
                          <Tooltip offset={-6}>
                            <div className="flex flex-col gap-0.5">
                              <span className="text-muted-foreground text-xs">
                                {format(result.accessionDate)}
                              </span>
                              <Link
                                to="/accessions/$id"
                                params={{ id: result.accessionId }}
                                className="text-blue-500 underline font-medium"
                              >
                                {result.accessionId}
                              </Link>
                            </div>
                          </Tooltip>
                        </TooltipTrigger>
                      </TableRAC.Cell>
                    );
                  })}
                </TableRAC.Row>
              )}
            </TableRAC.Body>
          </TableRAC>
        </Virtualizer>
      ) : (
        <div className="flex-1 overflow-auto">
          <TableRAC className="w-full">
            <TableRAC.Header className="bg-background sticky top-0 z-5">
              <TableRAC.Row>
                <TableRAC.Column isRowHeader width={150}>
                  Test
                </TableRAC.Column>
                <TableRAC.Column width={150}>Result</TableRAC.Column>
                <TableRAC.Column width={150}>Initial Value</TableRAC.Column>
                <TableRAC.Column width={150}>Latest Value</TableRAC.Column>
                <TableRAC.Column width={100}>Change</TableRAC.Column>
                <TableRAC.Column width={150}>Range</TableRAC.Column>
                <TableRAC.Column width={280}>Trend</TableRAC.Column>
              </TableRAC.Row>
            </TableRAC.Header>
            <TableRAC.Body
              items={filteredData.filter(
                (item) =>
                  item.testResultSpecType === "NUMERIC" &&
                  item.results.length > 1,
              )}
            >
              {(item) => {
                const chartData = prepareChartData(item);
                if (!chartData) return null;

                // Get the initial and latest results for display
                const initialResult = item.results[0];
                const latestResult = item.results[item.results.length - 1];
                const comparisonValue =
                  comparison === "previous"
                    ? latestResult.percentChange
                    : latestResult.percentFromInitial;

                // Get range values
                const rangeMin =
                  item.testResultSpec.rangeMin !== null
                    ? Number.parseFloat(String(item.testResultSpec.rangeMin))
                    : null;
                const rangeMax =
                  item.testResultSpec.rangeMax !== null
                    ? Number.parseFloat(String(item.testResultSpec.rangeMax))
                    : null;

                // Check if values are out of range
                const initialValue = initialResult.resultNumeric
                  ? Number.parseFloat(initialResult.resultNumeric.toString())
                  : null;
                const latestValue = latestResult.resultNumeric
                  ? Number.parseFloat(latestResult.resultNumeric.toString())
                  : null;

                const isInitialOutOfRange =
                  initialValue !== null &&
                  ((rangeMin !== null && initialValue < rangeMin) ||
                    (rangeMax !== null && initialValue > rangeMax));

                const isLatestOutOfRange =
                  latestValue !== null &&
                  ((rangeMin !== null && latestValue < rangeMin) ||
                    (rangeMax !== null && latestValue > rangeMax));

                return (
                  <TableRAC.Row
                    id={item.testResultSpecId}
                    className="hover:bg-muted/50"
                  >
                    <TableRAC.Cell className="font-medium">
                      {item.testSpecName}
                    </TableRAC.Cell>
                    <TableRAC.Cell className="text-sm">
                      {item.testResultSpecName}
                    </TableRAC.Cell>
                    <TableRAC.Cell
                      className={
                        isInitialOutOfRange
                          ? "text-red-500 font-medium"
                          : "font-medium"
                      }
                    >
                      {initialResult.resultNumeric?.toString() || "N/A"}
                    </TableRAC.Cell>
                    <TableRAC.Cell
                      className={
                        isLatestOutOfRange
                          ? "text-red-500 font-medium"
                          : "font-medium"
                      }
                    >
                      {latestResult.resultNumeric?.toString() || "N/A"}
                    </TableRAC.Cell>
                    <TableRAC.Cell>
                      {comparisonValue !== null &&
                        comparisonValue !== undefined && (
                          <Badge
                            color={
                              Math.abs(comparisonValue) > 50 ? "red" : "gray"
                            }
                          >
                            {comparisonValue > 0 ? "+" : ""}
                            {comparisonValue.toFixed(1)}%
                          </Badge>
                        )}
                    </TableRAC.Cell>
                    <TableRAC.Cell className="text-sm">
                      {(rangeMin !== null || rangeMax !== null) && (
                        <>
                          {rangeMin !== null ? rangeMin : "n/a"} -{" "}
                          {rangeMax !== null ? rangeMax : "n/a"}
                        </>
                      )}
                    </TableRAC.Cell>
                    <TableRAC.Cell>
                      <RangeSparkLineChart
                        className="h-16 w-[300px]"
                        data={chartData}
                        categories={["value"]}
                        index="date"
                        connectNulls={true}
                        rangeMin={rangeMin}
                        rangeMax={rangeMax}
                      />
                    </TableRAC.Cell>
                  </TableRAC.Row>
                );
              }}
            </TableRAC.Body>
          </TableRAC>
        </div>
      )}
    </div>
  );
}
